import i18next from "i18next";

import type { GraphConfig } from "../PaygapPayGradesOverview";

/**
 * Helper to get translated text for multilingual graph config fields.
 * Uses translation keys with fallback to English.
 */
const getTranslatedField = (key: string): { cz: string; sk: string; en: string } => {
  const t = i18next.t.bind(i18next);


return {
    cz: t(key, { lng: "cs" }),
    sk: t(key, { lng: "sk" }),
    en: t(key, { lng: "en" }),
  };
};

/**
 * Default graph configurations for paygap analysis charts.
 * These are used as fallback if no configurations are provided via API.
 */
export const getDefaultGraphConfigs = (): GraphConfig[] => [
  {
    graph_id: "seniority_gap",
    title: getTranslatedField("paygap.graphConfig.seniorityGap.title"),
    chart: "line_or_columns",
    x_axis: getTranslatedField("paygap.graphConfig.seniorityGap.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.seniorityGap.yAxis"),
    series: [
      {
        key: "seniority-1-difference",
        label: getTranslatedField("paygap.graphConfig.seniorityGap.level1"),
      },
      {
        key: "seniority-2-difference",
        label: getTranslatedField("paygap.graphConfig.seniorityGap.level2"),
      },
      {
        key: "seniority-3-difference",
        label: getTranslatedField("paygap.graphConfig.seniorityGap.level3"),
      },
      {
        key: "seniority-4-difference",
        label: getTranslatedField("paygap.graphConfig.seniorityGap.level4"),
      },
      {
        key: "seniority-5-difference",
        label: getTranslatedField("paygap.graphConfig.seniorityGap.level5"),
      },
    ],
  },
  {
    graph_id: "workforce_composition",
    title: getTranslatedField("paygap.graphConfig.workforceComposition.title"),
    chart: "kpi_or_bar",
    x_axis: getTranslatedField("paygap.graphConfig.workforceComposition.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.workforceComposition.yAxis"),
    series: [
      {
        key: "female-employees-percentage",
        label: getTranslatedField("paygap.graphConfig.workforceComposition.female"),
      },
      {
        key: "male-employees-percentage",
        label: getTranslatedField("paygap.graphConfig.workforceComposition.male"),
      },
    ],
  },
  {
    graph_id: "pay_gaps_overview",
    title: getTranslatedField("paygap.graphConfig.payGapsOverview.title"),
    chart: "bar",
    x_axis: getTranslatedField("paygap.graphConfig.payGapsOverview.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.payGapsOverview.yAxis"),
    series: [
      {
        key: "difference-average-income",
        label: getTranslatedField(
          "paygap.graphConfig.payGapsOverview.differenceAverage",
        ),
      },
      {
        key: "difference-basic-income",
        label: getTranslatedField(
          "paygap.graphConfig.payGapsOverview.differenceBasic",
        ),
      },
      {
        key: "gender-pay-gap",
        label: getTranslatedField("paygap.graphConfig.payGapsOverview.genderPayGap"),
      },
    ],
  },
  {
    graph_id: "variable_impact_adjusted",
    title: getTranslatedField("paygap.graphConfig.variableImpact.title"),
    chart: "bar",
    x_axis: getTranslatedField("paygap.graphConfig.variableImpact.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.variableImpact.yAxis"),
    series: [
      {
        key: "age-impact",
        label: getTranslatedField("paygap.graphConfig.variableImpact.age"),
      },
      {
        key: "years-of-service-impact",
        label: getTranslatedField(
          "paygap.graphConfig.variableImpact.yearsOfService",
        ),
      },
      {
        key: "experience-impact",
        label: getTranslatedField("paygap.graphConfig.variableImpact.experience"),
      },
      {
        key: "education-impact",
        label: getTranslatedField("paygap.graphConfig.variableImpact.education"),
      },
      {
        key: "seniority-impact",
        label: getTranslatedField("paygap.graphConfig.variableImpact.seniority"),
      },
      {
        key: "sex-impact",
        label: getTranslatedField("paygap.graphConfig.variableImpact.sex"),
      },
    ],
  },
  {
    graph_id: "quartiles_boxplot",
    title: getTranslatedField("paygap.graphConfig.quartilesBoxplot.title"),
    chart: "boxplot",
    x_axis: getTranslatedField("paygap.graphConfig.quartilesBoxplot.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.quartilesBoxplot.yAxis"),
    series: [
      {
        key: "quartile-minimum-male",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.minimumMale"),
      },
      {
        key: "quartile-first-male",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.q1Male"),
      },
      {
        key: "quartile-second-male",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.medianMale"),
      },
      {
        key: "quartile-third-male",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.q3Male"),
      },
      {
        key: "quartile-fourth-male",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.maximumMale"),
      },
      {
        key: "quartile-minimum-female",
        label: getTranslatedField(
          "paygap.graphConfig.quartilesBoxplot.minimumFemale",
        ),
      },
      {
        key: "quartile-first-female",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.q1Female"),
      },
      {
        key: "quartile-second-female",
        label: getTranslatedField(
          "paygap.graphConfig.quartilesBoxplot.medianFemale",
        ),
      },
      {
        key: "quartile-third-female",
        label: getTranslatedField("paygap.graphConfig.quartilesBoxplot.q3Female"),
      },
      {
        key: "quartile-fourth-female",
        label: getTranslatedField(
          "paygap.graphConfig.quartilesBoxplot.maximumFemale",
        ),
      },
    ],
  },
  {
    graph_id: "total_cost_by_gender",
    title: getTranslatedField("paygap.graphConfig.totalCostByGender.title"),
    chart: "stacked_bar",
    x_axis: getTranslatedField("paygap.graphConfig.totalCostByGender.xAxis"),
    y_axis: getTranslatedField("paygap.graphConfig.totalCostByGender.yAxis"),
    series: [
      {
        key: "total-cost-male",
        label: getTranslatedField("paygap.graphConfig.totalCostByGender.maleTotal"),
      },
      {
        key: "total-cost-female",
        label: getTranslatedField(
          "paygap.graphConfig.totalCostByGender.femaleTotal",
        ),
      },
      {
        key: "total-cost-male-percentage",
        label: getTranslatedField("paygap.graphConfig.totalCostByGender.maleShare"),
      },
      {
        key: "total-cost-female-percentage",
        label: getTranslatedField(
          "paygap.graphConfig.totalCostByGender.femaleShare",
        ),
      },
    ],
  },
];
